import { ThemeProvider } from "@mui/material";
import { useLocalStorage } from "@src/appV2/lib/utils";
import { useModalState } from "@src/appV2/redesign/system/ui-components";
import { getTheme } from "@src/appV2/redesign/system/ui-theme";
import { type ReactElement, useRef } from "react";

import { ConsoleModal, FloatingButton } from "./components";
import { useConsoleInterceptor, useDragPosition } from "./hooks";
import { type LogLevel, type MobileConsoleProps } from "./types";
import { useMobileConsole } from "./useMobileConsole";
import { getLogCountByLevel } from "./utils";

export function MobileConsole(props: MobileConsoleProps): ReactElement | undefined {
  const { maxLogs = 100 } = props;
  const modalState = useModalState();
  const logContainerRef = useRef<HTMLDivElement>(null);

  const [storedFilters, setStoredFilters] = useLocalStorage<LogLevel[]>(
    "mobile-console-active-filters",
    []
  );

  const activeFilters = new Set(storedFilters);
  const setActiveFilters = (filters: Set<LogLevel>) => {
    setStoredFilters([...filters]);
  };

  const { isVisible } = useMobileConsole({ maxLogs });

  const { logs, clearLogs } = useConsoleInterceptor({ maxLogs });
  const { position, isDragging, hasDragged, buttonRef, handleDragStart } = useDragPosition();

  if (!isVisible) {
    return undefined;
  }

  const logCounts = getLogCountByLevel(logs);

  const filteredLogs =
    activeFilters.size === 0 ? logs : logs.filter((log) => activeFilters.has(log.level));

  const toggleFilter = (level: LogLevel) => {
    const newFilters = new Set(activeFilters);
    if (newFilters.has(level)) {
      newFilters.delete(level);
    } else {
      newFilters.add(level);
    }

    setActiveFilters(newFilters);
  };

  const handleClearFilters = () => {
    setActiveFilters(new Set());
  };

  const handleExpand = () => {
    if (!hasDragged) {
      modalState.openModal();
    }
  };

  const content = modalState.modalIsOpen ? (
    <ConsoleModal
      logs={logs}
      filteredLogs={filteredLogs}
      logCounts={logCounts}
      activeFilters={activeFilters}
      logContainerRef={logContainerRef}
      modalState={modalState}
      onClearLogs={clearLogs}
      onClearFilters={handleClearFilters}
      onToggleFilter={toggleFilter}
    />
  ) : (
    <FloatingButton
      ref={buttonRef}
      position={position}
      isDragging={isDragging}
      onMouseDown={handleDragStart}
      onTouchStart={handleDragStart}
      onClick={handleExpand}
    />
  );

  return <ThemeProvider theme={getTheme()}>{content}</ThemeProvider>;
}
